import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "TarjetaUID";

const ORIGEN_UID = Object.freeze({
  CELULAR_HEX: "CELULAR_HEX",
  LECTOR_DECIMAL: "LECTOR_DECIMAL",
});

const MAXIMO_UID_4_BYTES = 4294967295;

/* =========================================================
   FUNCIONES AUXILIARES
========================================================= */

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

const escaparHTML = (valor = "") => {
  const caracteres = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };

  return String(valor).replace(
    /[&<>"']/g,
    (caracter) => caracteres[caracter]
  );
};

/**
 * Limpia un UID hexadecimal.
 *
 * 04:51:C4:B6:2E:02:89
 * -> 0451C4B62E0289
 */
const limpiarUIDHexadecimal = (valor = "") =>
  String(valor)
    .trim()
    .replace(/^0x/i, "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

/**
 * Limpia el código decimal entregado por el lector.
 */
const limpiarUIDDecimal = (valor = "") =>
  String(valor)
    .trim()
    .replace(/\D/g, "");

/**
 * Detecta automáticamente el tipo de código escrito.
 *
 * 3066319108
 * -> LECTOR_DECIMAL
 *
 * 0451C4B62E0289
 * -> CELULAR_HEX
 */
const detectarOrigenUID = (valor = "") => {
  const valorOriginal = String(valor).trim();

  const valorLimpio = valorOriginal
    .replace(/^0x/i, "")
    .replace(/[\s:-]/g, "")
    .toUpperCase();

  if (!valorLimpio) {
    return ORIGEN_UID.LECTOR_DECIMAL;
  }

  if (
    /^0x/i.test(valorOriginal) ||
    /[\s:-]/.test(valorOriginal) ||
    /[A-F]/.test(valorLimpio) ||
    valorLimpio.length > 10
  ) {
    return ORIGEN_UID.CELULAR_HEX;
  }

  return ORIGEN_UID.LECTOR_DECIMAL;
};

/**
 * Limpia la entrada según el origen detectado.
 */
const limpiarEntradaUID = (
  valor,
  origen
) => {
  if (origen === ORIGEN_UID.LECTOR_DECIMAL) {
    return limpiarUIDDecimal(valor);
  }

  return limpiarUIDHexadecimal(valor);
};

/**
 * Convierte el decimal del lector al UID hexadecimal
 * normalizado de cuatro bytes.
 *
 * 3066319108
 * -> B6C45104
 * -> 0451C4B6
 */
const convertirDecimalLectorAHex = (valor) => {
  const texto = limpiarUIDDecimal(valor);

  if (!texto) {
    return "";
  }

  const numero = Number(texto);

  if (
    !Number.isSafeInteger(numero) ||
    numero < 0 ||
    numero > MAXIMO_UID_4_BYTES
  ) {
    throw new Error(
      "El código del lector debe estar entre 0 y 4294967295."
    );
  }

  const hexadecimalInvertido = numero
    .toString(16)
    .padStart(8, "0")
    .toUpperCase();

  const bytes = hexadecimalInvertido.match(/.{2}/g);

  if (!bytes || bytes.length !== 4) {
    throw new Error(
      "No fue posible convertir el código del lector."
    );
  }

  return bytes.reverse().join("");
};

/**
 * Calcula el UID que almacenará el backend.
 */
const obtenerUIDNormalizado = (
  valor,
  origen
) => {
  if (origen === ORIGEN_UID.LECTOR_DECIMAL) {
    return convertirDecimalLectorAHex(valor);
  }

  const uidHexadecimal =
    limpiarUIDHexadecimal(valor);

  if (!uidHexadecimal) {
    return "";
  }

  if (uidHexadecimal.length < 8) {
    throw new Error(
      "El UID debe contener al menos cuatro bytes."
    );
  }

  if (uidHexadecimal.length % 2 !== 0) {
    throw new Error(
      "El UID hexadecimal debe contener bytes completos."
    );
  }

  return uidHexadecimal.slice(0, 8);
};

/**
 * Valida el código antes de enviarlo.
 */
const validarUID = (
  valor,
  origen
) => {
  try {
    const valorLimpio = limpiarEntradaUID(
      valor,
      origen
    );

    if (!valorLimpio) {
      return false;
    }

    if (origen === ORIGEN_UID.LECTOR_DECIMAL) {
      const numero = Number(valorLimpio);

      return (
        /^\d+$/.test(valorLimpio) &&
        Number.isSafeInteger(numero) &&
        numero >= 0 &&
        numero <= MAXIMO_UID_4_BYTES
      );
    }

    return (
      /^[0-9A-F]+$/.test(valorLimpio) &&
      valorLimpio.length >= 8 &&
      valorLimpio.length % 2 === 0
    );
  } catch (error) {
    return false;
  }
};

const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const obtenerPermisosModulo = () => {
  try {
    const permisosStorage =
      localStorage.getItem("permisos");

    const usuarioStorage =
      localStorage.getItem("usuario");

    const userStorage =
      localStorage.getItem("user");

    const usuariosStorage =
      localStorage.getItem("usuarios");

    const permisosData = permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};

    const modulos = Array.isArray(permisosData)
      ? permisosData
      : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

    return (
      modulos.find((modulo) => {
        const nombreModulo =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          "";

        return (
          normalizarTexto(nombreModulo) ===
          normalizarTexto(MODULO)
        );
      }) || {}
    );
  } catch (error) {
    console.error(
      "Error leyendo los permisos:",
      error
    );

    return {};
  }
};

/* =========================================================
   COMPONENTE
========================================================= */

const RegistrarTarjetaAprendiz = () => {
  const [
    documentoAprendiz,
    setDocumentoAprendiz,
  ] = useState("");

  const [
    nombreAprendiz,
    setNombreAprendiz,
  ] = useState("");

  const [aprendizId, setAprendizId] =
    useState("");

  const [tarjetaUID, setTarjetaUID] =
    useState("");

  /**
   * El origen se administra internamente.
   * No se muestra ningún selector al usuario.
   */
  const [origenUID, setOrigenUID] =
    useState(ORIGEN_UID.LECTOR_DECIMAL);

  const [verificando, setVerificando] =
    useState(false);

  const [guardando, setGuardando] =
    useState(false);

  const [leyendoNFC, setLeyendoNFC] =
    useState(false);

  const lectorNFCRef = useRef(null);
  const abortControllerRef = useRef(null);

  const navigate = useNavigate();

  const permisosModulo = useMemo(
    () => obtenerPermisosModulo(),
    []
  );

  const puedeCrear =
    permisosModulo?.puedeCrear === true ||
    permisosModulo?.crear === true;

  const aprendizVerificado = useMemo(
    () =>
      Boolean(
        aprendizId &&
        nombreAprendiz
      ),
    [aprendizId, nombreAprendiz]
  );

  const nfcDisponible = useMemo(
    () =>
      typeof window !== "undefined" &&
      "NDEFReader" in window,
    []
  );

  const contextoSeguro = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.isSecureContext,
    []
  );

  const uidValido = useMemo(
    () =>
      validarUID(
        tarjetaUID,
        origenUID
      ),
    [tarjetaUID, origenUID]
  );

  const uidNormalizadoVista = useMemo(() => {
    if (!uidValido) {
      return "";
    }

    try {
      return obtenerUIDNormalizado(
        tarjetaUID,
        origenUID
      );
    } catch (error) {
      return "";
    }
  }, [
    tarjetaUID,
    origenUID,
    uidValido,
  ]);

  const detenerLecturaNFC =
    useCallback(() => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      } catch (error) {
        console.error(
          "Error deteniendo NFC:",
          error
        );
      } finally {
        abortControllerRef.current = null;
        lectorNFCRef.current = null;
        setLeyendoNFC(false);
      }
    }, []);

  const limpiarFormulario =
    useCallback(() => {
      detenerLecturaNFC();

      setDocumentoAprendiz("");
      setNombreAprendiz("");
      setAprendizId("");
      setTarjetaUID("");
      setOrigenUID(
        ORIGEN_UID.LECTOR_DECIMAL
      );
    }, [detenerLecturaNFC]);

  const regresarListado =
    useCallback(() => {
      detenerLecturaNFC();

      navigate(
        "/tarjeta/listartarjetaaprendiz"
      );
    }, [
      detenerLecturaNFC,
      navigate,
    ]);

  useEffect(() => {
    return () => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      } catch (error) {
        console.error(
          "Error cerrando el lector NFC:",
          error
        );
      }
    };
  }, []);

  /* =======================================================
     VERIFICAR APRENDIZ
  ======================================================= */

  const verificarDocumentoAprendiz =
    useCallback(
      async (documento) => {
        const doc = String(
          documento || ""
        ).trim();

        if (
          !doc ||
          verificando ||
          guardando
        ) {
          return;
        }

        detenerLecturaNFC();

        setTarjetaUID("");
        setOrigenUID(
          ORIGEN_UID.LECTOR_DECIMAL
        );

        try {
          setVerificando(true);

          const response = await axios.get(
            `/api/aprendices/verificar/${encodeURIComponent(
              doc
            )}`,
            authConfig()
          );

          if (!response.data?.exists) {
            setNombreAprendiz("");
            setAprendizId("");

            await Swal.fire({
              icon: "error",
              title:
                "Aprendiz no encontrado",
              text:
                "El documento ingresado no corresponde a ningún aprendiz.",
              confirmButtonText: "Aceptar",
            });

            return;
          }

          const nombreCompleto =
            response.data?.nombreCompleto ||
            "";

          const idEncontrado =
            response.data?.aprendizId ||
            "";

          if (!idEncontrado) {
            setNombreAprendiz("");
            setAprendizId("");

            await Swal.fire({
              icon: "warning",
              title:
                "Información incompleta",
              text:
                "El aprendiz fue encontrado, pero no se recibió su identificador.",
              confirmButtonText: "Aceptar",
            });

            return;
          }

          setNombreAprendiz(
            nombreCompleto
          );

          setAprendizId(
            idEncontrado
          );

          await Swal.fire({
            icon: "success",
            title:
              "Aprendiz verificado",
            text: nombreCompleto,
            confirmButtonText: "Aceptar",
          });
        } catch (error) {
          console.error(
            "Error verificando el aprendiz:",
            error
          );

          setNombreAprendiz("");
          setAprendizId("");

          await Swal.fire({
            icon: "error",
            title:
              "Error al verificar",
            text:
              error.response?.data
                ?.message ||
              "No se pudo verificar el documento del aprendiz.",
            confirmButtonText: "Aceptar",
          });
        } finally {
          setVerificando(false);
        }
      },
      [
        detenerLecturaNFC,
        guardando,
        verificando,
      ]
    );

  /* =======================================================
     LEER DESDE WEB NFC
  ======================================================= */

  const iniciarLecturaNFC =
    useCallback(async () => {
      if (!puedeCrear) {
        await Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text:
            "No tiene permisos para asignar tarjetas.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!aprendizVerificado) {
        await Swal.fire({
          icon: "warning",
          title:
            "Aprendiz sin verificar",
          text:
            "Primero debes verificar el documento del aprendiz.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!nfcDisponible) {
        await Swal.fire({
          icon: "warning",
          title:
            "NFC no disponible",
          html: `
            <p>
              Este navegador no permite utilizar Web NFC.
            </p>

            <p class="mb-0">
              Abre la aplicación desde
              <strong>Google Chrome en Android</strong>
              y verifica que el celular tenga NFC.
            </p>
          `,
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!contextoSeguro) {
        await Swal.fire({
          icon: "warning",
          title:
            "Se requiere HTTPS",
          text:
            "La lectura NFC solamente funciona desde una conexión segura.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      try {
        detenerLecturaNFC();

        const abortController =
          new AbortController();

        const lector =
          new window.NDEFReader();

        abortControllerRef.current =
          abortController;

        lectorNFCRef.current =
          lector;

        lector.addEventListener(
          "reading",
          async (evento) => {
            const serialNumber =
              evento.serialNumber || "";

            const uidCompleto =
              limpiarUIDHexadecimal(
                serialNumber
              );

            if (!uidCompleto) {
              detenerLecturaNFC();

              await Swal.fire({
                icon: "warning",
                title:
                  "UID no disponible",
                text:
                  "La tarjeta fue detectada, pero el navegador no entregó su UID.",
                confirmButtonText:
                  "Aceptar",
              });

              return;
            }

            /**
             * Como el valor proviene directamente
             * de Web NFC, se marca internamente
             * como hexadecimal.
             */
            setOrigenUID(
              ORIGEN_UID.CELULAR_HEX
            );

            setTarjetaUID(
              uidCompleto
            );

            detenerLecturaNFC();

            let uidNormalizado = "";

            try {
              uidNormalizado =
                obtenerUIDNormalizado(
                  uidCompleto,
                  ORIGEN_UID.CELULAR_HEX
                );
            } catch (error) {
              console.error(
                "Error normalizando UID:",
                error
              );
            }

            await Swal.fire({
              icon: "success",
              title:
                "Tarjeta detectada",
              html: `
                <div class="text-start">
                  <div>
                    <strong>Código leído:</strong>
                    <span class="font-monospace">
                      ${escaparHTML(uidCompleto)}
                    </span>
                  </div>

                  ${
                    uidNormalizado
                      ? `
                        <div class="mt-2">
                          <strong>UID de identificación:</strong>
                          <span class="font-monospace">
                            ${escaparHTML(uidNormalizado)}
                          </span>
                        </div>
                      `
                      : ""
                  }

                  <p class="mt-3 mb-0 text-secondary">
                    La tarjeta todavía no ha sido guardada.
                  </p>
                </div>
              `,
              confirmButtonText: "Aceptar",
            });
          }
        );

        lector.addEventListener(
          "readingerror",
          async () => {
            detenerLecturaNFC();

            await Swal.fire({
              icon: "error",
              title:
                "Error de lectura",
              text:
                "La tarjeta fue detectada, pero el navegador no pudo procesarla.",
              confirmButtonText:
                "Aceptar",
            });
          }
        );

        setLeyendoNFC(true);

        await lector.scan({
          signal:
            abortController.signal,
        });

        await Swal.fire({
          icon: "info",
          title:
            "Lector NFC activo",
          html: `
            <p>
              Acerca la tarjeta a la parte posterior
              del celular.
            </p>

            <p class="mb-0">
              La tarjeta no se guardará automáticamente.
            </p>
          `,
          confirmButtonText: "Entendido",
        });
      } catch (error) {
        if (
          error?.name ===
          "AbortError"
        ) {
          return;
        }

        console.error(
          "Error iniciando NFC:",
          error
        );

        detenerLecturaNFC();

        let mensaje =
          "No se pudo iniciar el lector NFC.";

        switch (error?.name) {
          case "NotAllowedError":
            mensaje =
              "El permiso NFC fue rechazado. Autoriza el acceso desde Chrome.";
            break;

          case "NotSupportedError":
            mensaje =
              "El dispositivo o navegador no permite esta lectura NFC.";
            break;

          case "NotReadableError":
            mensaje =
              "El NFC está ocupado por otra aplicación o no está disponible.";
            break;

          case "InvalidStateError":
            mensaje =
              "Ya existe una lectura NFC activa.";
            break;

          case "SecurityError":
            mensaje =
              "La lectura NFC fue bloqueada. Verifica que la aplicación use HTTPS.";
            break;

          default:
            mensaje =
              error?.message ||
              "No se pudo iniciar el lector NFC.";
            break;
        }

        await Swal.fire({
          icon: "error",
          title: "Error NFC",
          text: mensaje,
          confirmButtonText: "Aceptar",
        });
      }
    }, [
      aprendizVerificado,
      contextoSeguro,
      detenerLecturaNFC,
      nfcDisponible,
      puedeCrear,
    ]);

  /* =======================================================
     GUARDAR TARJETA
  ======================================================= */

  const guardarTarjeta =
    useCallback(async () => {
      if (!puedeCrear) {
        await Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text:
            "No tiene permisos para asignar tarjetas.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!aprendizId) {
        await Swal.fire({
          icon: "warning",
          title:
            "Aprendiz sin verificar",
          text:
            "Debes verificar el documento del aprendiz.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      const uidOriginal =
        limpiarEntradaUID(
          tarjetaUID,
          origenUID
        );

      if (!uidOriginal) {
        await Swal.fire({
          icon: "warning",
          title: "UID requerido",
          text:
            "Debes escribir o leer el UID de la tarjeta.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (
        !validarUID(
          uidOriginal,
          origenUID
        )
      ) {
        await Swal.fire({
          icon: "warning",
          title: "UID no válido",
          text:
            origenUID ===
            ORIGEN_UID.LECTOR_DECIMAL
              ? "El código decimal debe estar entre 0 y 4294967295."
              : "El UID debe contener al menos cuatro bytes completos.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      let uidNormalizado;

      try {
        uidNormalizado =
          obtenerUIDNormalizado(
            uidOriginal,
            origenUID
          );
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title:
            "Error en el UID",
          text: error.message,
          confirmButtonText: "Aceptar",
        });

        return;
      }

      detenerLecturaNFC();

      const confirmacion =
        await Swal.fire({
          icon: "question",
          title:
            "Confirmar asignación",
          html: `
            <div class="text-start">
              <p>
                Revisa la información antes de asignar
                la tarjeta.
              </p>

              <div class="mb-2">
                <strong>Aprendiz:</strong>
                ${escaparHTML(nombreAprendiz)}
              </div>

              <div class="mb-2">
                <strong>Código leído:</strong>
                <span class="font-monospace">
                  ${escaparHTML(uidOriginal)}
                </span>
              </div>

              <div>
                <strong>UID de identificación:</strong>
                <span class="font-monospace">
                  ${escaparHTML(uidNormalizado)}
                </span>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText:
            "Sí, asignar tarjeta",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#212529",
          cancelButtonColor: "#6c757d",
          reverseButtons: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          focusCancel: true,
        });

      if (!confirmacion.isConfirmed) {
        return;
      }

      try {
        setGuardando(true);

        /**
         * origenUID no se muestra ni se almacena en
         * MongoDB. Solo ayuda al backend a interpretar
         * correctamente el valor recibido.
         */
        const nuevaAsignacion = {
          aprendizObjectId:
            aprendizId,

          tarjetaUID:
            uidOriginal,

          origenUID,
        };

        const response =
          await axios.post(
            "/api/tarjetas/add",
            nuevaAsignacion,
            authConfig()
          );

        const uidGuardado =
          response.data?.uidNormalizado ||
          response.data?.asignacion
            ?.tarjetaUID ||
          uidNormalizado;

        await Swal.fire({
          icon: "success",
          title:
            "Tarjeta asignada",
          html: `
            <div class="text-start">
              <p>
                La tarjeta fue asignada correctamente.
              </p>

              <div class="mb-2">
                <strong>Aprendiz:</strong>
                ${escaparHTML(nombreAprendiz)}
              </div>

              <div>
                <strong>UID guardado:</strong>
                <span class="font-monospace">
                  ${escaparHTML(uidGuardado)}
                </span>
              </div>
            </div>
          `,
          confirmButtonText: "Aceptar",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        limpiarFormulario();
        regresarListado();
      } catch (error) {
        console.error(
          "Error asignando la tarjeta:",
          error
        );

        await Swal.fire({
          icon: "error",
          title:
            "Error al asignar la tarjeta",
          text:
            error.response?.data
              ?.message ||
            error.response?.data
              ?.error ||
            "No se pudo asignar la tarjeta.",
          confirmButtonText: "Aceptar",
        });
      } finally {
        setGuardando(false);
      }
    }, [
      aprendizId,
      detenerLecturaNFC,
      limpiarFormulario,
      nombreAprendiz,
      origenUID,
      puedeCrear,
      regresarListado,
      tarjetaUID,
    ]);

  /* =======================================================
     EVENTOS
  ======================================================= */

  const handleSubmit = async (
    evento
  ) => {
    evento.preventDefault();

    await guardarTarjeta();
  };

  const handleDocumentoChange = (
    evento
  ) => {
    detenerLecturaNFC();

    setDocumentoAprendiz(
      evento.target.value
    );

    setNombreAprendiz("");
    setAprendizId("");
    setTarjetaUID("");

    setOrigenUID(
      ORIGEN_UID.LECTOR_DECIMAL
    );
  };

  const handleDocumentoKeyDown = (
    evento
  ) => {
    if (evento.key === "Enter") {
      evento.preventDefault();

      verificarDocumentoAprendiz(
        documentoAprendiz
      );
    }
  };

  const handleDocumentoBlur = () => {
    if (
      documentoAprendiz.trim() &&
      !aprendizId &&
      !verificando
    ) {
      verificarDocumentoAprendiz(
        documentoAprendiz
      );
    }
  };

  /**
   * Detecta automáticamente el formato escrito.
   */
  const handleTarjetaUIDChange = (
    evento
  ) => {
    const valorIngresado =
      evento.target.value;

    const origenDetectado =
      detectarOrigenUID(
        valorIngresado
      );

    const valorLimpio =
      limpiarEntradaUID(
        valorIngresado,
        origenDetectado
      );

    setOrigenUID(
      origenDetectado
    );

    setTarjetaUID(
      valorLimpio
    );
  };

  const procesoActivo =
    verificando || guardando;

  const formularioDeshabilitado =
    procesoActivo || !puedeCrear;

  /* =======================================================
     INTERFAZ
  ======================================================= */

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Encabezado */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Asignar Tarjeta
                </h5>

                <small className="text-secondary">
                  Verifica el aprendiz, lee o
                  escribe el UID y confirma la
                  asignación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={regresarListado}
                  disabled={procesoActivo}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2" />
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={limpiarFormulario}
                  disabled={
                    formularioDeshabilitado
                  }
                >
                  <i className="fa-solid fa-eraser me-2" />
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-3 p-md-4">
            {!puedeCrear && (
              <div className="alert alert-warning mb-4">
                <i className="fa-solid fa-triangle-exclamation me-2" />
                No tiene permisos para asignar
                tarjetas.
              </div>
            )}

            {!nfcDisponible && (
              <div className="alert alert-info mb-4">
                <i className="fa-solid fa-circle-info me-2" />

                <strong>
                  Web NFC no está disponible en
                  este navegador.
                </strong>

                <div className="mt-1">
                  Puedes utilizar el lector físico
                  o escribir manualmente el código.
                </div>
              </div>
            )}

            {nfcDisponible &&
              !contextoSeguro && (
                <div className="alert alert-warning mb-4">
                  <i className="fa-solid fa-lock me-2" />
                  La lectura NFC requiere HTTPS.
                </div>
              )}

            {procesoActivo && (
              <div className="text-center text-secondary py-3">
                <div
                  className="spinner-border"
                  role="status"
                  aria-label="Procesando"
                />

                <div className="mt-2">
                  {verificando
                    ? "Verificando aprendiz..."
                    : "Asignando tarjeta..."}
                </div>
              </div>
            )}

            {leyendoNFC && (
              <div className="alert alert-primary mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <i className="fa-solid fa-wifi me-2" />

                    <strong>
                      Lector NFC activo.
                    </strong>

                    <div className="mt-1">
                      Acerca la tarjeta a la parte
                      posterior del celular.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={detenerLecturaNFC}
                  >
                    <i className="fa-solid fa-circle-stop me-2" />
                    Detener
                  </button>
                </div>
              </div>
            )}

            <form
              id="form-tarjeta"
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="row g-3 align-items-end">
                {/* Documento */}
                <div className="col-12 col-md-3">
                  <label
                    htmlFor="documentoAprendiz"
                    className="form-label fw-semibold"
                  >
                    Documento Aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-id-card" />
                    </span>

                    <input
                      id="documentoAprendiz"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="form-control"
                      placeholder="Ej: 123456789"
                      value={documentoAprendiz}
                      onChange={
                        handleDocumentoChange
                      }
                      onBlur={
                        handleDocumentoBlur
                      }
                      onKeyDown={
                        handleDocumentoKeyDown
                      }
                      required
                      disabled={
                        formularioDeshabilitado
                      }
                    />

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        verificarDocumentoAprendiz(
                          documentoAprendiz
                        )
                      }
                      disabled={
                        formularioDeshabilitado ||
                        !documentoAprendiz.trim()
                      }
                    >
                      <i
                        className={`fa-solid ${
                          verificando
                            ? "fa-spinner fa-spin"
                            : "fa-magnifying-glass"
                        } me-2`}
                      />

                      Verificar
                    </button>
                  </div>

                  <div className="form-text">
                    Presiona <strong>Enter</strong>{" "}
                    o utiliza el botón Verificar.
                  </div>
                </div>

                {/* Nombre */}
                <div className="col-12 col-md-4">
                  <label
                    htmlFor="nombreAprendiz"
                    className="form-label fw-semibold"
                  >
                    Nombre Aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-user" />
                    </span>

                    <input
                      id="nombreAprendiz"
                      type="text"
                      className="form-control"
                      placeholder="Se completa automáticamente"
                      value={nombreAprendiz}
                      readOnly
                      disabled
                    />

                    <span
                      className={`input-group-text ${
                        aprendizVerificado
                          ? "text-success"
                          : "text-secondary"
                      }`}
                    >
                      <i
                        className={`fa-solid ${
                          aprendizVerificado
                            ? "fa-circle-check"
                            : "fa-circle-info"
                        }`}
                      />
                    </span>
                  </div>

                  <div className="form-text">
                    {aprendizVerificado
                      ? "Aprendiz verificado correctamente."
                      : "Primero verifica el documento."}
                  </div>
                </div>

                {/* UID */}
                <div className="col-12 col-md-5">
                  <label
                    htmlFor="tarjetaUID"
                    className="form-label fw-semibold"
                  >
                    UID de la Tarjeta
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-solid fa-id-badge" />
                    </span>

                    <input
                      id="tarjetaUID"
                      type="text"
                      autoComplete="off"
                      className="form-control text-uppercase"
                      placeholder="Lee o escribe el código de la tarjeta"
                      value={tarjetaUID}
                      onChange={
                        handleTarjetaUIDChange
                      }
                      required
                      disabled={
                        formularioDeshabilitado ||
                        !aprendizVerificado ||
                        leyendoNFC
                      }
                    />

                    <button
                      type="button"
                      className={
                        leyendoNFC
                          ? "btn btn-outline-danger"
                          : "btn btn-outline-primary"
                      }
                      onClick={
                        leyendoNFC
                          ? detenerLecturaNFC
                          : iniciarLecturaNFC
                      }
                      disabled={
                        formularioDeshabilitado ||
                        !aprendizVerificado ||
                        (!nfcDisponible &&
                          !leyendoNFC)
                      }
                    >
                      <i
                        className={`fa-solid ${
                          leyendoNFC
                            ? "fa-circle-stop"
                            : "fa-wifi"
                        } me-2`}
                      />

                      {leyendoNFC
                        ? "Detener"
                        : "Leer NFC"}
                    </button>
                  </div>

                  <div className="form-text">
                    La lectura no se guarda
                    automáticamente.
                  </div>
                </div>

                {/* Vista previa */}
                {tarjetaUID && (
                  <div className="col-12">
                    <div
                      className={`alert ${
                        uidValido
                          ? "alert-success"
                          : "alert-warning"
                      } mb-0`}
                    >
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        <div>
                          <strong>
                            Código recibido:
                          </strong>{" "}
                          <span className="font-monospace">
                            {tarjetaUID}
                          </span>
                        </div>

                        {uidNormalizadoVista && (
                          <div>
                            <strong>
                              UID de identificación:
                            </strong>{" "}
                            <span className="font-monospace">
                              {
                                uidNormalizadoVista
                              }
                            </span>
                          </div>
                        )}
                      </div>

                      {!uidValido && (
                        <div className="mt-2">
                          El código ingresado todavía
                          no tiene un formato válido.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Botones */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={regresarListado}
                      disabled={procesoActivo}
                    >
                      <i className="fa-solid fa-xmark me-2" />
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      className="btn btn-dark"
                      disabled={
                        formularioDeshabilitado ||
                        leyendoNFC ||
                        !aprendizVerificado ||
                        !uidValido
                      }
                    >
                      <i
                        className={`fa-solid ${
                          guardando
                            ? "fa-spinner fa-spin"
                            : "fa-link"
                        } me-2`}
                      />

                      {guardando
                        ? "Asignando..."
                        : "Asignar Tarjeta"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <hr className="my-4" />

            <div className="small text-secondary">
              <i className="fa-solid fa-circle-info me-2" />

              Después de leer o escribir el código,
              presiona{" "}
              <strong>Asignar Tarjeta</strong> y
              confirma la operación.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarTarjetaAprendiz;